import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { chatApi } from "../api/ChatApi";
import { v4 as uuidv4 } from "uuid";
import type { RootState } from "./index";
import { PROJECT_ID } from "../config";
import ChatHistoryApi from "../api/ChatHistoryApi";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "../utils/constants";
import { retryWithExponentialBackoff } from "../utils/retry";

export interface Source {
  score: number;
  type: "text" | "image" | null;
  document: string | null;
  uid?: string;
  metadata?: any;
}

export interface ChatMessage {
  id: string;
  sender: "user" | "agent";
  text: string;
  sources?: Source[];
  summary?: string;
  related_info?: string;
  suggested_followups?: string;
  backendMessageId?: string;
  isPersisted?: boolean;
  feedback?: boolean | null;
  streamingSteps?: StreamingStep[];
}

export interface Prompt {
  title: string;
  description: string;
}

export interface StreamingStep {
  step: string;
  message?: string;
  query_id?: string;
  query?: string;
  num_records?: number;
  documents?: any[];
  answer_length?: number;
  cited_documents?: any[];
  timestamp?: number;
}

interface ChatState {
  messages: ChatMessage[];
  prompts: Prompt[];
  chats: { id: string; title: string }[];
  activeChatId: string | null;
  activeSessionId: string | null;
  previousMessageId: string[];
  loading: boolean;
  loadingSession: boolean;
  isChatOpen: boolean;
  error: string | null;
  shouldRefreshHistory: boolean;
  streamingSteps: StreamingStep[];
  isStreaming: boolean;
}

const initialState: ChatState = {
  messages: [],
  prompts: [],
  chats: [],
  activeChatId: null,
  activeSessionId: null,
  previousMessageId: [],
  loading: false,
  loadingSession: false,
  isChatOpen: false,
  error: null,
  shouldRefreshHistory: false,
  streamingSteps: [],
  isStreaming: false,
};

//
// ✅ FETCH PROMPTS
//
export const fetchPrompts = createAsyncThunk<Prompt[], string | undefined>(
  "chat/fetchPrompts",
  async (accessToken) => {
    try {
      const data = await chatApi.getPromptQuestions(accessToken);
      const rawQuestions: string[] = Array.isArray(data?.questions)
        ? data.questions
        : [];

      const transformed: Prompt[] =
        rawQuestions.length > 0
          ? rawQuestions.map((q: string) => ({
              title: q,
              description: "",
            }))
          : [];

      return transformed;
    } catch (error) {
      toast.warning(ERROR_MESSAGES.LOAD_PROMPTS_FAILED);
      return [];
    }
  }
);

//
// ✅ LOAD SESSION MESSAGES
//
export const loadSession = createAsyncThunk<
  { messages: ChatMessage[]; sessionId: string },
  { sessionId: string; accessToken?: string },
  { state: RootState }
>(
  "chat/loadSession",
  async ({ sessionId, accessToken }, { rejectWithValue }) => {
    try {
      const response = await retryWithExponentialBackoff(() =>
        ChatHistoryApi.getMessages(sessionId, accessToken)
      );

      const sessionData = response.data;
      const turns = sessionData?.turns || [];

      // Transform turns to ChatMessage format with validation
      const messages: ChatMessage[] = [];

      turns.forEach((turnJson: string | object) => {
        // Support both string and object formats
        console.log(typeof turnJson);

        if (!turnJson) {
          return;
        }

        try {
          // If it's already an object, use it directly; otherwise parse the string
          const turn =
            typeof turnJson === "object"
              ? turnJson
              : JSON.parse(turnJson as string);

          // Validate turn structure
          if (!turn || typeof turn !== "object") {
            return;
          }

          // Add user message
          if (turn.user?.msg && typeof turn.user.msg === "string") {
            messages.push({
              id: uuidv4(),
              sender: "user",
              text: turn.user.msg,
            });
          }

          // Add AI message
          if (turn.ai?.msg && typeof turn.ai.msg === "string") {
            let sources = [];
            let summary = undefined;
            let related_info = undefined;
            let suggested_followups = undefined;
            const rawMessage = turn.ai.raw_message;

            // Try to parse raw_message for sources and additional fields
            if (rawMessage && typeof rawMessage === "string") {
              try {
                const parsed = JSON.parse(rawMessage);
                if (parsed) {
                  if (Array.isArray(parsed.sources)) {
                    sources = parsed.sources;
                  }
                  if (parsed.summary) {
                    summary = parsed.summary;
                  }
                  if (parsed.related_info) {
                    related_info = parsed.related_info;
                  }
                  if (parsed.suggested_followups) {
                    suggested_followups = parsed.suggested_followups;
                  }
                }
              } catch (e) {
                // Ignore parse errors for raw_message
              }
            }

            messages.push({
              id: uuidv4(),
              sender: "agent",
              text: turn.ai.msg,
              sources: sources,
              summary: summary,
              related_info: related_info,
              suggested_followups: suggested_followups,
              backendMessageId: turn.id,
              isPersisted: true,
              feedback: turn?.feedback ?? null,
              streamingSteps: JSON.parse(turn.ai.chain_of_thought || "[]"),
            });
          }
        } catch (e) {}
      });

      return { messages, sessionId };
    } catch (error: any) {
      toast.error(ERROR_MESSAGES.SESSION_LOAD_FAILED);
      return rejectWithValue(error?.message || "Failed to load session");
    }
  }
);

//
// ✅ SEND QUERY STREAM THUNK
//
export const sendQueryStream = createAsyncThunk<
  {
    agentMessage: ChatMessage;
    sessionId: string;
    messageId: string;
    isNewSession: boolean;
  },
  { userMessage: string; sessionId?: string; previousMessageIds?: string[] },
  { state: RootState; dispatch: any }
>(
  "chat/sendQueryStream",
  async (
    { userMessage, sessionId, previousMessageIds },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      if (
        !userMessage ||
        typeof userMessage !== "string" ||
        userMessage.trim() === ""
      ) {
        throw new Error("Query (userMessage) is required and cannot be empty.");
      }

      const state = getState();
      const sessionName = userMessage.slice(0, 50);
      const stateChat = (getState() as RootState).chat;

      const conversationHistory = stateChat.messages
        .map((m) => m.sender + ":\\n" + m.text)
        .filter((t) => t && t.trim() !== "");

      const effectiveSessionId = sessionId ?? stateChat.activeSessionId ?? "";

      // Clear previous streaming steps and set streaming flag
      dispatch(clearStreamingSteps());
      dispatch(setStreaming(true));

      console.log("Starting streaming query:", {
        userMessage,
        effectiveSessionId,
      });

      let finalResponse: any = null;

      // Call streaming API
      try {
        await chatApi.sendQueryStream(
          userMessage,
          conversationHistory.slice(0, -1),
          (step) => {
            // Add step to streaming steps
            dispatch(addStreamingStep({ ...step, timestamp: Date.now() }));

            if (step.step === "completed" && step.message) {
              finalResponse = step.message;
            }
          }
        );
      } catch (streamError: any) {
        console.error("Streaming error:", streamError);
        dispatch(setStreaming(false));
        dispatch(clearStreamingSteps());
        throw new Error(`Streaming failed: ${streamError.message}`);
      }

      if (!finalResponse) {
        console.error("No completed response received from stream");
        dispatch(setStreaming(false));
        dispatch(clearStreamingSteps());
        throw new Error("No completed response received");
      }

      console.log("Final response received:", finalResponse);

      const agentText = finalResponse?.answer || "";
      const newSessionId = finalResponse?.session_id || sessionId || "";
      const isNewSession = !sessionId && !!finalResponse?.session_id;

      // Create agent message with streaming steps
      const agentMessageId = uuidv4();
      const currentSteps = getState().chat.streamingSteps;
      console.log("📦 Saving steps to message:", currentSteps.length, "steps");
      const agentMessage = {
        id: agentMessageId,
        sender: "agent" as const,
        text: agentText,
        summary: finalResponse?.summary,
        related_info: finalResponse?.related_info,
        suggested_followups: finalResponse?.suggested_followups,
        sources: finalResponse?.sources,
        streamingSteps: [...currentSteps],
      };

      // Reset streaming flag but DON'T clear steps yet
      // Steps will be cleared in the fulfilled reducer after message is added
      dispatch(setStreaming(false));

      // Save to backend in background
      (async () => {
        try {
          const isFirstMessage =
            !effectiveSessionId || effectiveSessionId === "";
          let createRes;

          if (isFirstMessage) {
            createRes = await chatApi.createMessage({
              project_id: PROJECT_ID,
              session_name: sessionName,
              question: userMessage,
              ai_message: agentText,
              raw_message: JSON.stringify(finalResponse),
              chain_of_thought: JSON.stringify(agentMessage.streamingSteps),
            });
          } else {
            createRes = await chatApi.createMessage({
              session_id: effectiveSessionId,
              question: userMessage,
              ai_message: agentText,
              raw_message: JSON.stringify(finalResponse),
              chain_of_thought: JSON.stringify(agentMessage.streamingSteps),
            });
          }

          const finalSessionId =
            createRes?.session_id || newSessionId || effectiveSessionId || "";
          const finalMessageId = createRes?.message_id || "";
          const wasNewSession = isFirstMessage && !!createRes?.session_id;

          if (finalSessionId || finalMessageId) {
            dispatch(
              applyCreateResult({
                sessionId: finalSessionId,
                messageId: finalMessageId,
                isNewSession: wasNewSession,
                agentMessageId,
              })
            );
          }
        } catch (e) {
          console.error("Failed to save message:", e);
        }
      })();

      return {
        agentMessage,
        sessionId: newSessionId,
        messageId: "",
        isNewSession,
      };
    } catch (error: any) {
      dispatch(setStreaming(false));
      toast.error(ERROR_MESSAGES.SEND_MESSAGE_FAILED);
      return rejectWithValue(error?.message || "Failed to send query");
    }
  }
);

//
// ✅ SEND QUERY THUNK
//
export const sendQuery = createAsyncThunk<
  {
    agentMessage: ChatMessage;
    sessionId: string;
    messageId: string;
    isNewSession: boolean;
  },
  { userMessage: string; sessionId?: string; previousMessageIds?: string[] },
  { state: RootState; dispatch: any }
>(
  "chat/sendQuery",
  async (
    { userMessage, sessionId, previousMessageIds },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      if (
        !userMessage ||
        typeof userMessage !== "string" ||
        userMessage.trim() === ""
      ) {
        throw new Error("Query (userMessage) is required and cannot be empty.");
      }
      const state = getState();
      const userId = state.auth?.user?.homeAccountId || "anonymous";
      const sessionName = userMessage.slice(0, 50); // First 50 chars as session name

      // 1. Call /query first
      const stateChat = (getState() as RootState).chat;
      // Backend expects conversation_history as array of prior message strings (not IDs)
      const conversationHistory = stateChat.messages
        .map((m) => m.sender + ":\n" + m.text)
        .filter((t) => t && t.trim() !== "");

      // Chain of backend message_ids still needed for previous_message_id field in create
      const previousMessageIdChain =
        previousMessageIds ?? stateChat.previousMessageId ?? [];
      const effectiveSessionId = sessionId ?? stateChat.activeSessionId ?? "";
      const queryRes = await chatApi.sendQuery(
        userMessage,
        conversationHistory.slice(0, -1)
      );

      const agentText = queryRes?.answer || "";
      const newSessionId = queryRes?.session_id || sessionId || "";
      const isNewSession = !sessionId && !!queryRes?.session_id;

      // 2. Show agent response in chat immediately with all sections
      const agentMessageId = uuidv4(); // Generate ID upfront
      const agentMessage = {
        id: agentMessageId,
        sender: "agent" as const,
        text: agentText,
        summary: queryRes?.summary,
        related_info: queryRes?.related_info,
        suggested_followups: queryRes?.suggested_followups,
        sources: queryRes?.sources,
      }; // 3. Call /messages/create in the background (do not block UI)
      (async () => {
        try {
          // Determine if this is first message or subsequent
          const isFirstMessage =
            !effectiveSessionId || effectiveSessionId === "";

          let createRes;
          if (isFirstMessage) {
            // First message: create new session
            createRes = await chatApi.createMessage({
              project_id: PROJECT_ID,
              session_name: sessionName,
              question: userMessage,
              ai_message: agentText,
              raw_message: JSON.stringify(queryRes),
              chain_of_thought: "",
            });
          } else {
            // Subsequent message: use existing session
            createRes = await chatApi.createMessage({
              session_id: effectiveSessionId,
              question: userMessage,
              ai_message: agentText,
              raw_message: JSON.stringify(queryRes),
              chain_of_thought: "",
            });
          }
          // Update state with real session and message IDs
          const finalSessionId =
            createRes?.session_id || newSessionId || effectiveSessionId || "";
          const finalMessageId = createRes?.message_id || "";

          // isNewSession should be true only if this was the first message that created a session
          const wasNewSession = isFirstMessage && !!createRes?.session_id;

          if (finalSessionId || finalMessageId) {
            dispatch(
              applyCreateResult({
                sessionId: finalSessionId,
                messageId: finalMessageId,
                isNewSession: wasNewSession,
                agentMessageId, // Pass the frontend UUID to find the correct message
              })
            );
          }
        } catch (e) {
          // Optionally handle error (e.g., dispatch error toast)
        }
      })();

      // 4. Return agent message and session info for reducer
      return {
        agentMessage,
        sessionId: newSessionId,
        messageId: "", // Will be updated by background applyCreateResult when create returns
        isNewSession,
      };
    } catch (error: any) {
      toast.error(ERROR_MESSAGES.SEND_MESSAGE_FAILED);
      return rejectWithValue(error?.message || "Failed to send query");
    }
  }
);

//
// ✅ SLICE
//
const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    addUserMessage: (state: ChatState, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    addAgentMessage: (state: ChatState, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    applyCreateResult: (
      state: ChatState,
      action: PayloadAction<{
        sessionId: string;
        messageId: string;
        isNewSession?: boolean;
        agentMessageId?: string; // Frontend UUID of the agent message to attach backend ID to
      }>
    ) => {
      const { sessionId, messageId, isNewSession, agentMessageId } =
        action.payload;
      if (sessionId) {
        state.activeSessionId = sessionId;
      }
      if (messageId) {
        state.previousMessageId = [...state.previousMessageId, messageId];
        // Attach backend message_id to the specific agent message (identified by frontend UUID)
        if (agentMessageId) {
          const targetMessage = state.messages.find(
            (msg) => msg.id === agentMessageId
          );
          if (targetMessage && targetMessage.sender === "agent") {
            targetMessage.backendMessageId = messageId;
            targetMessage.isPersisted = true; // Mark as persisted
          }
        } else {
          // Fallback: attach to last agent message (legacy behavior)
          const lastMessage = state.messages[state.messages.length - 1];
          if (lastMessage && lastMessage.sender === "agent") {
            lastMessage.backendMessageId = messageId;
            lastMessage.isPersisted = true; // Mark as persisted
          }
        }
      }
      // Trigger chat history refresh ONLY when a NEW session is created
      // Do NOT refresh on every message in an existing session
      if (isNewSession) {
        state.shouldRefreshHistory = true;
      }
    },
    clearMessages: (state: ChatState) => {
      state.messages = [];
    },
    setActiveChat: (state: ChatState, action: PayloadAction<string>) => {
      state.activeChatId = action.payload;
    },
    clearChat: (state: ChatState) => {
      state.messages = [];
      state.activeChatId = null;
      state.activeSessionId = null;
      state.previousMessageId = [];
    },
    createChat: (state: ChatState, action: PayloadAction<string>) => {
      const newChat = {
        id: uuidv4(),
        title:
          action.payload || `New Chat - ${new Date().toLocaleTimeString()}`,
      };
      state.chats.unshift(newChat);
      state.activeChatId = newChat.id;
      state.isChatOpen = true;
      state.messages = [];
      state.activeSessionId = null;
      state.previousMessageId = [];
      state.shouldRefreshHistory = true; // Refresh history to fetch new chat from backend
    },
    resetHistoryRefreshFlag: (state: ChatState) => {
      state.shouldRefreshHistory = false;
    },
    addStreamingStep: (
      state: ChatState,
      action: PayloadAction<StreamingStep>
    ) => {
      console.log("Adding streaming step to Redux:", action.payload);
      state.streamingSteps.push(action.payload);
      console.log("Total steps now:", state.streamingSteps.length);
    },
    clearStreamingSteps: (state: ChatState) => {
      state.streamingSteps = [];
    },
    setStreaming: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrompts.pending, (state: ChatState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPrompts.fulfilled,
        (state: ChatState, action: PayloadAction<Prompt[]>) => {
          state.loading = false;
          state.prompts = action.payload;
        }
      )
      .addCase(fetchPrompts.rejected, (state: ChatState, action: any) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to fetch prompts.";
      })
      .addCase(loadSession.pending, (state: ChatState) => {
        state.loadingSession = true;
        state.error = null;
      })
      .addCase(
        loadSession.fulfilled,
        (
          state: ChatState,
          action: PayloadAction<{ messages: ChatMessage[]; sessionId: string }>
        ) => {
          state.loadingSession = false;
          state.messages = action.payload.messages;
          state.activeSessionId = action.payload.sessionId;
          state.isChatOpen = true;
          // Extract message IDs from loaded messages for continuing conversation
          state.previousMessageId = action.payload.messages
            .filter((msg) => msg.sender === "agent" && msg.backendMessageId)
            .map((msg) => msg.backendMessageId!);
        }
      )
      .addCase(loadSession.rejected, (state: ChatState, action: any) => {
        state.loadingSession = false;
        state.error = action.error?.message || "Failed to load session.";
      })
      .addCase(sendQuery.pending, (state: ChatState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        sendQuery.fulfilled,
        (
          state: ChatState,
          action: PayloadAction<{
            agentMessage: ChatMessage;
            sessionId: string;
            messageId: string;
            isNewSession: boolean;
          }>
        ) => {
          state.loading = false;
          state.messages.push(action.payload.agentMessage);
          if (action.payload.sessionId) {
            state.activeSessionId = action.payload.sessionId;
          }
          // Only accumulate message id if we actually have one
          if (action.payload.messageId) {
            state.previousMessageId = [
              ...state.previousMessageId,
              action.payload.messageId,
            ];
          }
          // Do not toggle history refresh here; we trigger it when create returns
        }
      )
      .addCase(sendQuery.rejected, (state: ChatState, action: any) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to send query.";
        state.messages.push({
          id: uuidv4(),
          sender: "agent",
          text: "Oops !! Looks like some services are not working as expected. Pls report this issue to the admin using Help Icon.",
        });
      })
      .addCase(sendQueryStream.pending, (state: ChatState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        sendQueryStream.fulfilled,
        (
          state: ChatState,
          action: PayloadAction<{
            agentMessage: ChatMessage;
            sessionId: string;
            messageId: string;
            isNewSession: boolean;
          }>
        ) => {
          state.loading = false;
          console.log(
            "✅ Adding message with steps:",
            action.payload.agentMessage.streamingSteps?.length || 0,
            "steps"
          );
          state.messages.push(action.payload.agentMessage);
          // DON'T clear global steps immediately - keep them for a moment to ensure smooth transition
          // They will be cleared on next query start
          if (action.payload.sessionId) {
            state.activeSessionId = action.payload.sessionId;
          }
          if (action.payload.messageId) {
            state.previousMessageId = [
              ...state.previousMessageId,
              action.payload.messageId,
            ];
          }
        }
      )
      .addCase(sendQueryStream.rejected, (state: ChatState, action: any) => {
        state.loading = false;
        state.isStreaming = false;
        state.error = action.error?.message || "Failed to send query.";
        state.messages.push({
          id: uuidv4(),
          sender: "agent",
          text: "Oops !! Looks like some services are not working as expected. Pls report this issue to the admin using Help Icon.",
        });
      });
  },
});

export const {
  addUserMessage,
  addAgentMessage,
  applyCreateResult,
  clearMessages,
  setActiveChat,
  clearChat,
  createChat,
  resetHistoryRefreshFlag,
  addStreamingStep,
  clearStreamingSteps,
  setStreaming,
} = chatSlice.actions;

export default chatSlice.reducer;
